import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { routeTree } from './router';
import RouteError from './components/RouteError';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Polling is opt-in per query (e.g. Alerts sets 3s, Health sets
      // 5s, the heavy DB-View aggregations set 5min). The previous
      // default of 5_000 silently hot-polled any query that didn't
      // override it — which on the 10-tab DB View dashboard meant
      // ~10 Mongo aggregations every 5 seconds for any user with the
      // page open. That alone was enough to make Mongo's wiredTiger
      // cache balloon to its default 50%-of-RAM ceiling.
      refetchInterval: false,
      refetchOnWindowFocus: true,
      retry: 1,
      // 60s freshness window — refocus inside the window doesn't trigger
      // a refetch storm if the user tabs back and forth.
      staleTime: 60_000,
    },
  },
});

// defaultErrorComponent replaces TanStack's built-in "Something went wrong!"
// — a bare string with no message — for every route at once. See
// components/RouteError.tsx for why that mattered.
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultErrorComponent: RouteError,
});

declare module '@tanstack/react-router' {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Register {
    router: typeof router;
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </React.StrictMode>,
);
